/**
 * Mesures dictées chiffrées par l'API (statut `estimé_ia`, PRD §12) côté partie : contrôle de forme, encodage compact pour
 * le lien de partage et décodage défensif. Aucune borne n'est écrite ici : elles sont dérivées du contenu chargé
 * (catalogue, baseline, paramètres `estimate_bound_*`), comme côté serveur (backend/src/context.ts).
 *
 * Un lien de partage peut être forgé : la mesure reçue est re-contrôlée (types, longueurs, bornes) et marquée
 * « reçue par lien, non revérifiée par le serveur » ; hors bornes → ignorée (jamais ramenée silencieusement dans les bornes).
 */
import type { BasketItem, Measure, MeasureCategory, MeasureEffects, ParamSet, Profile, SpendType } from "../../engine/src/types.js";
import type { Content } from "./content-types.js";
import { CATEGORY_LABELS } from "./content-types.js";

/** Forme compacte dans le lien (`ia` du SharePayload) : ni note ni raisonnement (trop longs), le reste à l'identique. */
export interface CompactEstimate {
  /** 12 premiers caractères hexadécimaux de la clé de cache serveur (id = `ia_<k>`). */
  k: string;
  l: string;
  d?: string;
  c: MeasureCategory;
  /** [coût, bas, haut] en Md€/an en régime. */
  co: [number, number, number];
  r: number;
  /** [libellé, bas, haut, défaut] de l'hypothèse dominante. */
  h: [string, number, number, number];
  st?: SpendType;
  fx: MeasureEffects;
  /** [partisans, critiques]. */
  cl: [string, string];
}

/** Au plus N mesures dictées par lien de partage (taille de l'URL). */
export const MAX_SHARED_ESTIMATES = 5;

const SCALAR_CHANNELS = ["g", "u", "pov", "coh", "conf", "eu", "co2", "lab", "trade", "asso", "apul", "pp", "maj"] as const;
const PARAM_BOUNDS: Record<string, string> = { pp: "estimate_bound_pp_pct", maj: "estimate_bound_maj_pt" };
const BASKET: readonly BasketItem[] = ["bread", "fuel", "rent", "energy"];
const SPEND: readonly SpendType[] = ["investment", "transfer", "payroll"];

export interface EstimateBounds {
  cost_bn: number;
  channels: Record<string, number>;
  svc: number;
  prices: number;
  political_cost: Partial<Record<MeasureCategory, number>>;
}

const absMax = (p: Profile | undefined): number => (p === undefined ? 0 : Array.isArray(p) ? Math.max(0, ...p.map(Math.abs)) : Math.abs(p));
const paramValue = (params: ParamSet, id: string): number => Math.abs(params[id]?.value ?? 0);

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** Bornes dérivées du contenu (mêmes règles que le contrôle serveur). */
export function estimateBounds(content: Pick<Content, "measures" | "baseline" | "params">): EstimateBounds {
  const channels: Record<string, number> = {};
  let svc = 0;
  let prices = paramValue(content.params, "estimate_bound_prices_pct");
  let cost = 0;
  const pcs: Partial<Record<MeasureCategory, number[]>> = {};
  for (const m of content.measures) {
    if (m.status === "estimé_ia") continue;
    cost = Math.max(cost, absMax(m.cost), absMax(m.cost_low), absMax(m.cost_high));
    for (const ch of SCALAR_CHANNELS) {
      const v = (m.fx as Record<string, Profile | undefined>)[ch];
      if (v !== undefined) channels[ch] = Math.max(channels[ch] ?? 0, absMax(v));
    }
    for (const p of Object.values(m.fx.svc ?? {})) svc = Math.max(svc, absMax(p));
    for (const p of Object.values(m.fx.prices ?? {})) prices = Math.max(prices, absMax(p));
    if (m.status === "sourced") (pcs[m.category] ??= []).push(m.political_cost);
  }
  for (const [ch, pid] of Object.entries(PARAM_BOUNDS)) channels[ch] = Math.max(channels[ch] ?? 0, paramValue(content.params, pid));
  const st = content.baseline.state;
  for (const x of st?.missions ?? []) cost = Math.max(cost, Math.abs(x.credits?.value ?? 0));
  for (const x of st?.revenues ?? []) cost = Math.max(cost, Math.abs(x.amount?.value ?? 0));
  const political_cost = Object.fromEntries(Object.entries(pcs).map(([c, xs]) => [c, median(xs ?? [])])) as Partial<Record<MeasureCategory, number>>;
  return { cost_bn: cost, channels, svc, prices, political_cost };
}

const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const isStr = (x: unknown, max: number, min = 1): x is string => typeof x === "string" && x.trim().length >= min && x.length <= max;

/** Statuts des mesures chiffrées par IA (aperçu provisoire ou chiffrage sourcé, ADR 0013). */
export const IA_STATUSES: readonly Measure["status"][] = ["estimé_ia", "estimé_ia_source", "estimé_ia_une_source"];
export const isIaMeasure = (m: Pick<Measure, "status">): boolean => IA_STATUSES.includes(m.status);

/** URL de source affichable (http/https seulement). */
const isHttpUrl = (u: unknown): boolean => typeof u === "string" && /^https?:\/\/[^\s"'<>]+$/i.test(u);

/** Contrôle d'une mesure `estimé_ia*` (reçue de l'API ou d'un lien) : null si un champ sort de la forme ou des bornes. */
export function checkEstimated(m: Measure, b: EstimateBounds): string | null {
  if (!isIaMeasure(m)) return "statut";
  if (m.status === "estimé_ia" ? m.sources.length > 0 : m.sources.length === 0 || m.sources.length > 8 || !m.sources.every((s) => isHttpUrl(s.url))) return "sources";
  if (!/^ia_[a-f0-9]{12}$/.test(m.id)) return "id";
  if (!(m.category in CATEGORY_LABELS)) return "catégorie";
  if (!isStr(m.label, 140, 3) || (m.description !== undefined && !isStr(m.description, 600))) return "libellé";
  if (!isStr(m.claims?.supporters, 400) || !isStr(m.claims?.critics, 400)) return "arguments";
  const c = m.cost;
  const lo = m.cost_low;
  const hi = m.cost_high;
  if (!isNum(c) || !isNum(lo) || !isNum(hi) || !(lo <= c && c <= hi) || Math.max(Math.abs(lo), Math.abs(hi)) > b.cost_bn) return "coût";
  if (!Number.isInteger(m.ramp) || m.ramp < 0 || m.ramp > 5) return "montée en charge";
  const h = m.hyp;
  if (!h || !isStr(h.label, 200, 3) || ![h.low, h.high, h.default].every((x) => isNum(x) && x >= 0 && x <= 100) || !(h.low <= h.default && h.default <= h.high)) return "hypothèse";
  if ((h.applies_to ?? "cost_leak") !== "cost_leak" || h.range !== "estimated") return "hypothèse";
  if (m.spend_type !== undefined && !SPEND.includes(m.spend_type)) return "nature de dépense";
  if (m.breakdown !== undefined || m.variants !== undefined || m.breakdown_complete !== undefined || m.once || m.derivation) return "champ réservé au catalogue";
  for (const [k, v] of Object.entries(m.fx)) {
    if (k === "svc") {
      for (const p of Object.values(v as Record<string, unknown>)) if (!isNum(p) || Math.abs(p) > b.svc) return "effet service";
      continue;
    }
    if (k === "prices") {
      for (const [item, p] of Object.entries(v as Record<string, unknown>)) if (!BASKET.includes(item as BasketItem) || !isNum(p) || Math.abs(p) > b.prices) return "effet prix";
      continue;
    }
    const bound = b.channels[k];
    if (bound === undefined || !(SCALAR_CHANNELS as readonly string[]).includes(k) || !isNum(v) || Math.abs(v) > bound) return `effet ${k}`;
  }
  return null;
}

export function encodeEstimate(m: Measure): CompactEstimate {
  const h = m.hyp!;
  return {
    k: m.id.slice(3),
    l: m.label,
    ...(m.description ? { d: m.description } : {}),
    c: m.category,
    co: [m.cost as number, m.cost_low as number, m.cost_high as number],
    r: m.ramp,
    h: [h.label, h.low, h.high, h.default],
    ...(m.spend_type ? { st: m.spend_type } : {}),
    fx: m.fx,
    cl: [m.claims.supporters, m.claims.critics],
  };
}

/**
 * Reconstruit et contrôle une mesure d'un lien ; null si la forme ou les bornes ne sont pas respectées. Un chiffrage
 * sourcé partagé par lien revient en `estimé_ia` sans sources : le lien ne les transporte pas et pourrait être forgé.
 */
export function decodeEstimate(x: unknown, b: EstimateBounds): Measure | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Partial<CompactEstimate>;
  if (typeof o.k !== "string" || !Array.isArray(o.co) || o.co.length !== 3 || !Array.isArray(o.h) || o.h.length !== 4 || !Array.isArray(o.cl) || o.cl.length !== 2) return null;
  if (!o.fx || typeof o.fx !== "object" || Array.isArray(o.fx) || typeof o.c !== "string") return null;
  const cat = o.c as MeasureCategory;
  const m: Measure = {
    id: `ia_${o.k}`,
    label: String(o.l ?? ""),
    ...(o.d !== undefined ? { description: String(o.d) } : {}),
    category: cat,
    cost: o.co[0] as number,
    cost_low: o.co[1] as number,
    cost_high: o.co[2] as number,
    ramp: o.r as number,
    political_cost: b.political_cost[cat] ?? 0,
    hyp: { label: String(o.h[0]), low: o.h[1] as number, high: o.h[2] as number, default: o.h[3] as number, applies_to: "cost_leak", range: "estimated" },
    ...(o.st !== undefined ? { spend_type: o.st } : {}),
    fx: o.fx,
    exclusive_with: [],
    sources: [],
    claims: { supporters: String(o.cl[0]), critics: String(o.cl[1]) },
    note: "Estimation IA non sourcée reçue par un lien de partage : non revérifiée par le serveur. Coût politique = médiane des mesures sourcées de la catégorie.",
    status: "estimé_ia",
  };
  return checkEstimated(m, b) === null ? m : null;
}
